import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuthStore } from '../stores/authStore';
import { useUserDashboard, useExamHistory, useUserBadges } from '../hooks/useUsers';
import { 
  User, 
  Mail, 
  Phone, 
  BookOpen, 
  Zap, 
  Flame, 
  Trophy,
  Calendar,
  Clock,
  TrendingUp,
  Award,
  Settings,
  LogOut
} from 'lucide-react';
import { 
  FadeIn, 
  ScaleIn, 
  AnimatedCard,
  ProgressBar,
  StaggerContainer,
  StaggerItem
} from '../components/ui/AnimatedCard';

export function Profile() {
  const { user, logout } = useAuthStore();
  const { data: dashboard } = useUserDashboard();
  const { data: history } = useExamHistory();
  const { data: badges } = useUserBadges();
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'badges'>('overview');

  if (!user) {
    return (
      <FadeIn>
        <div className="text-center py-16">
          <User className="w-16 h-16 text-slate-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Not logged in</h2>
          <p className="text-slate-500 mb-4">Please login to view your profile</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-xl font-medium"
          >
            Login
          </Link>
        </div>
      </FadeIn>
    );
  }

  const stats = dashboard?.stats;

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <FadeIn>
        <div className="bg-gradient-to-br from-primary-600 to-secondary-600 rounded-2xl p-8 text-white">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center text-4xl font-bold backdrop-blur-sm"
            >
              {user.avatar ? (
                <img src={user.avatar} alt={user.name} className="w-full h-full rounded-full object-cover" />
              ) : (
                user.name.charAt(0).toUpperCase()
              )}
            </motion.div>
            
            <div className="text-center md:text-left flex-1">
              <h1 className="text-2xl font-bold">{user.name}</h1>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mt-2 text-primary-100">
                {user.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-4 h-4" />
                    {user.phone}
                  </span>
                )}
                {user.email && (
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {user.email}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-3 mt-3">
                <span className="px-3 py-1 bg-white/20 rounded-full text-sm backdrop-blur-sm">
                  {dashboard?.user?.class?.name || 'Class not set'}
                </span>
                {user.isPremium && (
                  <span className="px-3 py-1 bg-amber-400/30 text-amber-200 rounded-full text-sm backdrop-blur-sm">
                    ⭐ Premium
                  </span>
                )}
              </div>
            </div>

            <div className="flex gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-3 bg-white/20 rounded-xl hover:bg-white/30 transition-colors backdrop-blur-sm"
              >
                <Settings className="w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={logout}
                className="p-3 bg-white/20 rounded-xl hover:bg-red-500/30 transition-colors backdrop-blur-sm"
              >
                <LogOut className="w-5 h-5" />
              </motion.button>
            </div>
          </div>
        </div>
      </FadeIn>

      {/* Stats Cards */}
      <StaggerContainer className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Points', value: stats?.totalPoints || 0, icon: Zap, color: 'text-amber-600 bg-amber-100' },
          { label: 'Exams', value: stats?.totalExams || 0, icon: BookOpen, color: 'text-primary-600 bg-primary-100' },
          { label: 'Avg Score', value: `${Math.round(stats?.averageScore || 0)}%`, icon: TrendingUp, color: 'text-secondary-600 bg-secondary-100' },
          { label: 'Streak', value: stats?.streakCount || 0, icon: Flame, color: 'text-orange-600 bg-orange-100' },
        ].map((stat) => (
          <StaggerItem key={stat.label}>
            <AnimatedCard className="p-4 text-center">
              <div className={`inline-flex items-center justify-center w-10 h-10 ${stat.color} rounded-xl mb-2`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <div className="text-xl font-bold text-slate-900">{stat.value}</div>
              <div className="text-xs text-slate-500">{stat.label}</div>
            </AnimatedCard>
          </StaggerItem>
        ))}
      </StaggerContainer>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {[
          { key: 'overview', label: 'Overview', icon: User },
          { key: 'history', label: 'Exam History', icon: Clock },
          { key: 'badges', label: 'Badges', icon: Award },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {activeTab === 'overview' && (
          <FadeIn>
            <div className="space-y-6">
              {/* Recent Activity */}
              <AnimatedCard className="p-6">
                <h3 className="font-bold text-slate-900 mb-4">Recent Activity</h3>
                {dashboard?.recentExams?.length === 0 ? (
                  <p className="text-slate-500 text-center py-8">No exams taken yet. Start practicing!</p>
                ) : (
                  <div className="space-y-3">
                    {dashboard?.recentExams?.map((attempt: any) => (
                      <div key={attempt.id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          (attempt.score / attempt.totalMarks) >= 0.4 
                            ? 'bg-secondary-100 text-secondary-600' 
                            : 'bg-red-100 text-red-600'
                        }`}>
                          <Trophy className="w-5 h-5" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-slate-900">{attempt.exam?.title}</div>
                          <div className="text-sm text-slate-500">
                            {attempt.exam?.subject?.name} • {new Date(attempt.submittedAt).toLocaleDateString()}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-slate-900">
                            {Math.round((attempt.score / attempt.totalMarks) * 100)}%
                          </div>
                          <div className="text-xs text-slate-500">{attempt.score}/{attempt.totalMarks}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </AnimatedCard>

              {/* Progress */}
              <AnimatedCard className="p-6">
                <h3 className="font-bold text-slate-900 mb-4">Your Progress</h3>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Overall Performance</span>
                      <span className="font-semibold text-slate-900">{Math.round(stats?.averageScore || 0)}%</span>
                    </div>
                    <ProgressBar progress={stats?.averageScore || 0} />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-slate-600">Points to Next Level</span>
                      <span className="font-semibold text-slate-900">
                        {stats?.totalPoints || 0} / {Math.ceil((stats?.totalPoints || 0) / 100) * 100}
                      </span>
                    </div>
                    <ProgressBar progress={((stats?.totalPoints || 0) % 100)} />
                  </div>
                </div>
              </AnimatedCard>
            </div>
          </FadeIn>
        )}

        {activeTab === 'history' && (
          <FadeIn>
            <div className="space-y-3">
              {history?.length === 0 ? (
                <AnimatedCard className="p-8 text-center">
                  <Clock className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-slate-900 mb-2">No exam history</h3>
                  <p className="text-slate-500">Take your first exam to see your history here</p>
                </AnimatedCard>
              ) : (
                history?.map((attempt: any) => (
                  <AnimatedCard key={attempt.id} className="p-4">
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        (attempt.score / attempt.totalMarks) >= 0.4 
                          ? 'bg-secondary-100 text-secondary-600' 
                          : 'bg-red-100 text-red-600'
                      }`}>
                        <Trophy className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-slate-900">{attempt.exam?.title}</h4>
                        <div className="flex items-center gap-3 text-sm text-slate-500">
                          <span>{attempt.exam?.subject?.name}</span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(attempt.submittedAt).toLocaleDateString()}
                          </span>
                          <span>•</span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {Math.floor(attempt.timeSpentSeconds / 60)}m {attempt.timeSpentSeconds % 60}s
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`text-lg font-bold ${
                          (attempt.score / attempt.totalMarks) >= 0.4 
                            ? 'text-secondary-600' 
                            : 'text-red-600'
                        }`}>
                          {Math.round((attempt.score / attempt.totalMarks) * 100)}%
                        </div>
                        <div className="text-xs text-slate-500">{attempt.score}/{attempt.totalMarks}</div>
                      </div>
                    </div>
                  </AnimatedCard>
                ))
              )}
            </div>
          </FadeIn>
        )}

        {activeTab === 'badges' && (
          <FadeIn>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {badges?.length === 0 ? (
                <div className="col-span-full text-center py-8">
                  <Award className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-slate-900 mb-2">No badges yet</h3>
                  <p className="text-slate-500">Complete exams and earn achievements to collect badges</p>
                </div>
              ) : (
                badges?.map((userBadge: any) => (
                  <AnimatedCard key={userBadge.id} className="p-4 text-center">
                    <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Award className="w-8 h-8 text-white" />
                    </div>
                    <h4 className="font-semibold text-slate-900 text-sm">{userBadge.badge?.name}</h4>
                    <p className="text-xs text-slate-500 mt-1">{userBadge.badge?.description}</p>
                    <div className="mt-2 text-xs text-amber-600 font-medium">
                      +{userBadge.badge?.pointsReward} points
                    </div>
                  </AnimatedCard>
                ))
              )}
            </div>
          </FadeIn>
        )}
      </div>
    </div>
  );
}
